import { formatDate, localDayIndex } from '../../utils/dates';

export interface CompetitionTiming {
  /** Short line for the card: "5 days left", "Opens tomorrow", "Ended Oct 19, 2026". */
  label: string;
  /** Share of the open window already gone, 0 to 1. Only for open competitions with both dates. */
  progress: number | null;
  /** Three days or fewer before an open competition closes. */
  urgent: boolean;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Day-based timing for a competition card. start/end are DATE values (local calendar days). */
export function competitionTiming(
  c: { status?: string; start_date?: string | null; end_date?: string | null },
  now: Date = new Date(),
): CompetitionTiming | null {
  const today = localDayIndex(now)!;
  const start = localDayIndex(c.start_date);
  const end = localDayIndex(c.end_date);
  const status = (c.status || '').toLowerCase();

  if (status === 'new') {
    if (start === null) return { label: 'Opening soon', progress: null, urgent: false };
    const until = start - today;
    if (until <= 0) return { label: 'Opening soon', progress: null, urgent: false };
    if (until === 1) return { label: 'Opens tomorrow', progress: null, urgent: false };
    if (until <= 30) return { label: `Opens in ${plural(until, 'day')}`, progress: null, urgent: false };
    return { label: `Opens ${formatDate(c.start_date)}`, progress: null, urgent: false };
  }

  if (status === 'open') {
    if (end === null) return null;
    const left = end - today;
    const progress = start !== null && end > start
      ? Math.min(1, Math.max(0, (today - start) / (end - start)))
      : null;
    if (left < 0) return { label: 'Closing soon', progress: 1, urgent: true };
    const urgent = left <= 3;
    if (left === 0) return { label: 'Last day', progress, urgent };
    if (left <= 30) return { label: `${plural(left, 'day')} left`, progress, urgent };
    return { label: `Closes ${formatDate(c.end_date)}`, progress, urgent };
  }

  if (end === null) return null;
  return { label: `Ended ${formatDate(c.end_date)}`, progress: null, urgent: false };
}
